/**
 * Agent-applied filter state for OpenPositionsPanel — separate from the
 * user's manual search query so typing in the search box is never
 * overwritten or blocked by voice-tool filters.
 */
import type { OpenPositionsIntent } from '../stores/panelIntentStore';
import type { Lexicon } from './primitives/lexicon';
import type { RoleTaxonomyIndex } from './roleTaxonomy';
import { resolveRoleIntent } from './roleTaxonomy';

/** Filters applied by the voice agent (shown as removable chips). */
export interface OpenPositionsAgentFilters {
  roleIntent?: string;
  location?: string;
  /** Keyword search from the agent — not the user's search-box query. */
  search?: string;
}

export interface AgentFilterChip {
  id: keyof OpenPositionsAgentFilters;
  label: string;
  value: string;
}

export function hasAgentFilters(filters: OpenPositionsAgentFilters): boolean {
  return Boolean(
    filters.roleIntent?.trim() ||
      filters.location?.trim() ||
      filters.search?.trim(),
  );
}

export function clearAgentFilterField(
  filters: OpenPositionsAgentFilters,
  field: keyof OpenPositionsAgentFilters,
): OpenPositionsAgentFilters {
  const next = { ...filters };
  delete next[field];
  return next;
}

export function clearAllAgentFilters(): OpenPositionsAgentFilters {
  return {};
}

/** Human label for a roleIntent id from the taxonomy index. */
export function roleIntentLabel(
  roleIntentId: string,
  roleIndex: RoleTaxonomyIndex,
): string {
  const entry =
    roleIndex.byId.get(roleIntentId) ??
    roleIndex.byId.get(roleIntentId.replace(/\s+/g, '_'));
  return entry?.label ?? roleIntentId.replace(/_/g, ' ');
}

/** Build removable chip descriptors for the active agent filters. */
export function buildAgentFilterChips(
  filters: OpenPositionsAgentFilters,
  roleIndex: RoleTaxonomyIndex,
): AgentFilterChip[] {
  const chips: AgentFilterChip[] = [];
  if (filters.roleIntent?.trim()) {
    chips.push({
      id: 'roleIntent',
      label: 'Role',
      value: roleIntentLabel(filters.roleIntent.trim(), roleIndex),
    });
  }
  if (filters.location?.trim()) {
    chips.push({
      id: 'location',
      label: 'Location',
      value: filters.location.trim(),
    });
  }
  if (filters.search?.trim()) {
    chips.push({
      id: 'search',
      label: 'Search',
      value: filters.search.trim(),
    });
  }
  return chips;
}

export interface ApplyOpenPositionsIntentResult {
  agentFilters: OpenPositionsAgentFilters;
  selectedId?: number | null;
  department?: string;
  track?: string;
  /** Reset user search when a new agent command arrives. */
  resetUserQuery: boolean;
}

/**
 * Map a one-shot panelIntentStore command into local panel state.
 * Agent keyword/location filters are kept separate from the user query.
 */
export function applyOpenPositionsIntent(
  intent: OpenPositionsIntent,
  jobs: readonly { id: number; title: string }[],
  departmentLexicon: Lexicon,
  trackLexicon: Lexicon,
  roleIndex: RoleTaxonomyIndex,
  findJobByTitle: (
    jobs: readonly { id: number; title: string }[],
    title: string,
  ) => { id: number } | undefined,
): ApplyOpenPositionsIntentResult {
  const agentFilters: OpenPositionsAgentFilters = {};
  const result: ApplyOpenPositionsIntentResult = {
    agentFilters,
    resetUserQuery: true,
  };

  if (typeof intent.selectedJobId === 'number') {
    result.selectedId = intent.selectedJobId;
  } else if (
    typeof intent.selectedJobTitle === 'string' &&
    intent.selectedJobTitle.trim()
  ) {
    const match = findJobByTitle(jobs, intent.selectedJobTitle.trim());
    if (match) result.selectedId = match.id;
  } else if (intent.selectedJobId === null) {
    result.selectedId = null;
  }

  if (typeof intent.roleIntent === 'string' && intent.roleIntent.trim()) {
    const resolved =
      resolveRoleIntent(intent.roleIntent.trim(), roleIndex) ??
      intent.roleIntent.trim();
    agentFilters.roleIntent = resolved;
  }

  if (typeof intent.location === 'string' && intent.location.trim()) {
    agentFilters.location = intent.location.trim();
  }

  if (typeof intent.search === 'string' && intent.search.trim()) {
    agentFilters.search = intent.search.trim();
  }

  if (typeof intent.department === 'string') {
    const normalised = departmentLexicon.normalize(intent.department);
    result.department = normalised ?? 'All';
    if (!normalised && !agentFilters.search) {
      agentFilters.search = intent.department.trim();
    }
  }

  if (typeof intent.track === 'string') {
    const normalisedTrack = trackLexicon.normalize(intent.track);
    result.track = normalisedTrack ?? 'All';
    if (!normalisedTrack && !agentFilters.search) {
      agentFilters.search = intent.track.trim();
    }
  }

  result.agentFilters = agentFilters;
  return result;
}
